import { STEP_NUMBERS } from "@/app/context/ProjectProvider/constants";
import HomeIcon from "@icons/home/home-white.svg";
import RefineIcon from "@icons/refine/refine-white.svg";
import LaunchIcon from "@icons/launch/launch_white.svg";
import { URLS } from "@/constants/constants";

export const GTM_VIDEO_GENERATION = "Generate Video";
export const VIDEO_GENERATION = "videoGeneration";
export const VIDEO_SPEC_TITLE = "Video Specs";
export const VIDEO = "Video";
export const PROMPT_GUIDELINE = "Prompt guidelines";
export const CREATE_NEW_VIDEO = "Create new video";
export const VIDEO_LENGTH = ":08 seconds";

export const VIDEO_SINGED_URL_TIMEOUT = 50 * 60 * 1000;

// setTimeout delay constants (in milliseconds)
export const VIDEO_STATUS_POLLING_INTERVAL = 10000;
export const FOCUS_DELAY_DEFAULT = 500;
export const FOCUS_DELAY_SHORT = 300;

export const VIDEO_GENERATION_STEPS_CONSTANTS = {
  STEP_NAMES: {
    1: "Create new project",
    2: "Add inputs",
    3: "Review video",
  },
};

export const VIDEO_GENERATION_STEPS = [
  { step: STEP_NUMBERS.ONE, label: VIDEO_GENERATION_STEPS_CONSTANTS.STEP_NAMES[1] },
  { step: STEP_NUMBERS.TWO, label: VIDEO_GENERATION_STEPS_CONSTANTS.STEP_NAMES[2] },
  { step: STEP_NUMBERS.THREE, label: VIDEO_GENERATION_STEPS_CONSTANTS.STEP_NAMES[3] },
];

export const VIDEO_PROJECT_CONSTANTS = {
  TITLE: "Create Your Project",
  HEADING_ID: "videoProjectHeadingID",
  DESCRIPTION:
    "Here you’ll choose your video size, video length, and video resolution. Many options are selected by default, but feel free to customize your setting for each new campaign.",
};

export const VIDEO_EXTENSION = "mp4";

export const VIDEO_INPUTS_CONSTANTS = {
  TITLE: "Add Project Details",
  HEADING_ID: "videoInputHeadingID",
  DESCRIPTION:
    "Choose a script to generate your video or prompt from scratch. We recommend a minimum of 250 words to get your concept across. You can also upload a start or end frame to set the overall tone of the video.",
  ADDITIONAL_DESCRIPTION: "Keep in mind: Video generation may take a few minutes, but will appear in your Saved Work area when finished.",
};

export const RAI_DOWNLOAD_MODAL_DESCRIPTION =
  "Your content has not passed our Responsible AI check. By choosing to proceed you accept all responsibility.";

export const VIDEO_REVIEWS_CONSTANTS = {
  TITLE: "Review Your Video",
  HEADING_ID: "videoReviewHeadingID",
  DESCRIPTION:
    "Here is where you can view your video or download it to your device. You’ll also be able to access it from your Saved Work area at any time. Keep in mind: Video is intended for concepting only and must go through standard Mondelez legal and brand approvals before any production or external use.",
  CREATE_ANOTHER_VIDEO: "Create another video",
  NEXT_ACTIVITY: {
    title: "What would you like to do next?",
    activities: [
      {
        href: URLS.HOME,
        title: "Go to dashboard",
        description: "Use or explore other creative features of AIDA.",
        icon: HomeIcon,
        pageName: VIDEO_GENERATION_STEPS_CONSTANTS.STEP_NAMES[3],
      },
      {
        href: "",
        title: "Refine prompt",
        description: "Make edits to your prompt and generate a new video.",
        icon: RefineIcon,
        pageName: VIDEO_GENERATION_STEPS_CONSTANTS.STEP_NAMES[3],
        className: "event_refine_concept_click",
      },
      {
        href: "",
        title: "Create new video",
        description: "Generate another video from a new prompt.",
        icon: LaunchIcon,
        pageName: VIDEO_GENERATION_STEPS_CONSTANTS.STEP_NAMES[3],
        className: "event_refine_concept_click",
      },
    ],
  },
  LOADING_TEXT: "Your Video is \nGenerating.",
  SUB_LOADING_TEXT: "The video will appear here and also in your Saved Work when complete.",
  DOWNLOAD_BUTTON: "Download MP4",
  REPLACE_VIDEO: "Replace video",
  VIDEO_PLAYER_ERROR: "The player is currently unavailable. Please download your video to view.",
  VIDEO_FAILED_STATUS: "Unfortunately,\n your video\n generation failed",
  TRY_BUTTON: "Try again",
  VIDEO_TRANSCRIPT_RETRY_BUTTON: "Generate again",
  REPLACE_MODAL_TITLE: "Want to keep any of the videos?",
  REPLACE_MODAL_DESCRIPTION:
    "So as not to overwrite your work, we recommend downloading any assets you want to keep now. Please keep in mind token usage and the overall impact on sustainability.",
  REPLACE_MODAL_YES_TEXT: "Yes, replace",
  REPLACE_MODAL_NO_TEXT: "No, return",
  VIDEO_TRANSCRIPT: "Video transcript",
  VIDEO_TRANSCRIPT_FAILED: "Transcripts are unavailable.",
  VIDEO_TRANSCRIPT_DOWNLOAD_NOTE: "Note that your transcript will be automatically downloaded with your file. ",
};

export const GENERATE_VIDEO_CONSTANTS: Record<number, object> = {
  1: VIDEO_PROJECT_CONSTANTS,
  2: VIDEO_INPUTS_CONSTANTS,
  3: {
    TITLE: VIDEO_REVIEWS_CONSTANTS.TITLE,
    DESCRIPTION: VIDEO_REVIEWS_CONSTANTS.DESCRIPTION,
    HEADING_ID: VIDEO_REVIEWS_CONSTANTS.HEADING_ID,
  },
};

export const VIDEO_GUIDELINES = [
  "Information should be unique to the campaign",
  "Do not enter sensitive, confidential or proprietary information (e.g., patents, product recipes)",
  "Do not enter unethical or inaccurate information",
  "Always follow inclusive language guidelines",
];

export const VIDEO_GENERATION_STEPS_TITLE = {
  1: VIDEO_PROJECT_CONSTANTS.TITLE,
  2: VIDEO_INPUTS_CONSTANTS.TITLE,
  3: VIDEO_REVIEWS_CONSTANTS.TITLE,
};

export const VIDEO_STATUS_ERROR_CATEGORY = {
  AIDA_FAILURE: "AIDA_FAILURE",
  GOOGLE_API_FAILURE: "GOOGLE_API_FAILURE",
  GOOGLE_RAI_FAILURE: "GOOGLE_RAI_FAILURE",
} as const;

export const GOOGLE_VIDEO_API_FAILURE_ERROR = "We're unable to generate your video at this time. Please try again.";

export const VIDEO_ARIA_LABELS = {
  VIDEO_GENERATED_PROMPT_PREFIX: "Video generated based on prompt:",
  NEW_VIDEO_GENERATED_PROMPT_PREFIX: "New video generated based on prompt:",
  DEFAULT_VIDEO_PLAYER: "Generated video player",
  COPY_VIDEO_TRANSCRIPT: "Copy video transcript",
} as const;

export const VIDEO_TRANSCRIPT_COPY_BUTTON_FOCUS_CLASS = "focus-visible";

export const getVideoAriaLabel = (description?: string): string =>
  description ? `${VIDEO_ARIA_LABELS.VIDEO_GENERATED_PROMPT_PREFIX} ${description}` : VIDEO_ARIA_LABELS.DEFAULT_VIDEO_PLAYER;

export const getNewVideoAnnouncement = (description: string = "your prompt"): string => {
  return `${VIDEO_ARIA_LABELS.NEW_VIDEO_GENERATED_PROMPT_PREFIX} ${description}`;
};
